import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SendIcon from "@mui/icons-material/Send";
import { GoogleGenerativeAI } from "@google/generative-ai";

/**
 * Chat component for a React website.
 * Provides a collapsible chat window that interacts with Gemini AI.
 */
const Chat = () => {
  // State variables
  const [chatText, setChatText] = useState(""); // Current text input in the chat
  const [chatLog, setChatLog] = useState([]); // Array of chat messages
  const [chatExpanded, setChatExpanded] = useState(false); // Whether the chat window is expanded
  const [chatOpacity, setChatOpacity] = useState(1); // Opacity of the chat window
  const [chatWaitingResponse, setChatWaitingResponse] = useState(false); // Block user input while waiting for a response
  const timerRef = useRef(null);  // Timer for automatic chat expansion
  const chatLogRef = useRef(null); // Ref to scroll the chat log

  // Refs for Gemini AI
  const genAI = useRef(null);
  const model = useRef(null);
  const chat = useRef(null);
  const GEMINI_AWS = true;

  const GEMINI_API_KEY = "";

    /**
   * Initializes Gemini AI.
   */
  const initGeminiAi = () => {
    try {
        genAI.current = new GoogleGenerativeAI(GEMINI_API_KEY);
        model.current = genAI.current.getGenerativeModel({
          model: "gemini-1.5-flash",
        });
        chat.current = model.current.startChat({
          history: [
            {
              role: "user",
              parts: [{ text: "Hello" }],
            },
            {
              role: "model",
              parts: [
                {
                  text: "System prompt: You are a customer service AI model, respond to the customer in most helpful fashion. User may send messages in Finnish, English or Swedish. Respond in the same language.",
                },
              ],
            },
          ],
        });
      } catch (error) {
        console.error("Error initializing gemini AI:", error);
        setChatLog([
          ...chatLog,
          {
            role: "model",
            text: "AI-assistant is currently unavailable. Try again later.",
          },
        ]);
      }
  };

  const sendGeminiMessageAWS = async () => {
    try {
      const response = await fetch("https://lwtfku0k49.execute-api.eu-north-1.amazonaws.com/dev/", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify([...chatLog, {role: "user", text: chatText}])
      });
      if (!response.ok) {
        console.error("Failed to fetch data from lambda", response.status);
        throw Error(response.status);
      }
      const json = await response.json();
      //console.log("aws response",json);
      return JSON.parse(json.body).response;
    } catch (error) {
      console.error("Failed to fetch data from lambda", error);
      return null;
    }
  }

  /**
   * Gets response from Gemini AI to the current chat message and updates the chat log.
   */
  const sendMessageGemini = async () => {
    if (chatText === "") return; // Don't send empty messages

    if (genAI.current === null && !GEMINI_AWS) {
        initGeminiAi();
    }

    try {
        // Send message to Gemini
        if (GEMINI_AWS) {
          //console.log(chatLog)
          const responseMsg = await sendGeminiMessageAWS();
          if (!responseMsg) { throw Error() }
          setChatLog((prevChatLog) => [...prevChatLog, { role: "model", text: responseMsg }]);
        }
        else
        {
          const result = await chat.current.sendMessage(chatText);
          const response = result.response;
  
           // Add AI response to chat log
           setChatLog((prevChatLog) => [...prevChatLog, { role: "model", text: response.text() }]);
        }
        setChatText(""); // Clear the input field
    } catch (error) {
      console.error("Error while retrieving gemini response:", error);
      setChatLog([
        ...chatLog,
        {
          role: "model",
          text: "AI-assistant is currently unavailable. Try again later.",
        },
      ]);
    }
    setChatWaitingResponse(false);
  };

  /**
   * Sends the current chat text to the AI and updates the chat log.
   */
  const chatSendMessage = () => {
    if (chatText === "") return; // Don't send empty messages
    // Add user message to chat log
    setChatLog((prevChatLog) => [...prevChatLog, { role: "user", text: chatText }]);
    sendMessageGemini(); // Send message to Gemini
    setChatWaitingResponse(true); // Block user input while waiting for response
  };

  /**
   * Handles the expansion/collapse of the chat window.
   */
  const onChatExpanded = (event, value) => {
    //console.log("Chat expanded:", value);
    setChatExpanded(value);
    if (timerRef.current) clearTimeout(timerRef.current); // Cancel the popup timer if user has already expanded view
  };

  /**
   * Handles changes to the chat input text.
   * @param {Event} e - The input change event.
   */
  const chatTextChange = (e) => {
    setChatText(e.target.value);
  };

  /**
   * Handles keypress events in the chat input.
   * Sends the message when the Enter key is pressed.
   * @param {Event} e - The keypress event.
   */
  const onChatKeypress = (e) => {
    if (e.key === "Enter") {
      chatSendMessage();
    }
  };

  /**
   * Effect to automatically expand the chat window after 5 seconds on initial load.
   */
  useEffect(() => {
    timerRef.current = setTimeout(() => {
       setChatExpanded(true);
    }, 5000);

    return () => clearTimeout(timerRef.current); // Cleanup timer on unmount
  }, []);

  /**
   * Effect to update the chat window's opacity based on its expanded state.
   */
  useEffect(() => {
    chatExpanded ? setChatOpacity(1) : setChatOpacity(0.8);
  }, [chatExpanded]);

  /**
   * Effect to scroll the chat window to bottom on new messages
   */
  useEffect(() =>{
    if (chatLogRef.current) {
    chatLogRef.current.scrollTop = chatLogRef.current.scrollHeight;
    }
  }, [chatLog]);

  return (
    <Box id="chatBox" position={"absolute"} right={"15px"} bottom={"15px"} sx={{ boxShadow: 5}}>
      <Accordion
        expanded={chatExpanded}
        onChange={onChatExpanded}
        sx={{ width: "300px", opacity: chatOpacity }}
      >
        <AccordionSummary
          aria-controls="chat-panel-content"
          id="chat-panel-header"
          sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}
          expandIcon={<ArrowDropDownIcon />}
        >
          
          <Typography >Chatbot Antti</Typography>
        </AccordionSummary>
        <AccordionDetails
          id="chat-panel-content"
          sx={{ maxHeight: "300px", overflowY: "scroll" }}
          ref={chatLogRef}
        >
          <Typography>
            Hei, Olen AI-chatbot Antti. <br />Kirjoita viestisi tähän niin vastaan
            mahdollisimman pian.
          </Typography>
          {chatLog.map((item, id) => (
            <Box key={id} sx={{
               backgroundColor: item.role === "user" ? "#e0e0e0" : "#bdbdbd", // Darker speech bubble
               borderRadius: "5px",
               padding: "8px",
               marginBottom: "2px"
            }}>
                <Typography>
                <b>{item.role === "user" ? "Sinä: " : "AI: "}</b> {item.text}
                </Typography>
            </Box>
          ))}
        </AccordionDetails>
        <Box id="chatFooter"  sx={{display: "flex", m: "2px"}}>
          <TextField
            sx={{ flexGrow: 1}}
            id="chat-textfield"
            onKeyPress={onChatKeypress}
            value={chatText}
            variant="outlined"
            onChange={chatTextChange}
            disabled={chatWaitingResponse}
          />
          <Button
          
            className="chatButton"
            onClick={() => {
              chatSendMessage();
            }}
            variant="outlined"
            disabled={chatWaitingResponse}
          >
            <SendIcon />
          </Button>
        </Box>
      </Accordion>
    </Box>
  );
};

export default Chat;
